/**
 * Suggested Actions Transformer
 *
 * Converts <suggested-actions> XML blocks found in message content into
 * inline HTML that flows naturally through the Markdown → typewriter
 * rendering pipeline. The resulting HTML uses data attributes for event
 * delegation so no framework-level component wiring is needed.
 *
 * XML Structure:
 *   <suggested-actions>
 *     <prompt>Optional prompt text</prompt>
 *     <action>Button label 1</action>
 *     <action>Button label 2</action>
 *   </suggested-actions>
 */

const BLOCK_PATTERN = /<suggested-actions>([\s\S]*?)<\/suggested-actions>/g;
const BLOCK_OPEN_TAG = '<suggested-actions';
const PROMPT_PATTERN = /<prompt>([\s\S]*?)<\/prompt>/;
const ACTION_PATTERN = /<action>([\s\S]*?)<\/action>/g;

/**
 * Escapes special HTML characters in user-facing text to prevent XSS
 * when injecting into raw HTML strings.
 */
export function escapeHtml(str) {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Extracts prompt and action labels from the inner content of a
 * <suggested-actions> block.
 */
function parseActionBlock(innerXml) {
  const promptMatch = innerXml.match(PROMPT_PATTERN);
  const actionMatches = [...innerXml.matchAll(ACTION_PATTERN)];

  const prompt = promptMatch ? promptMatch[1].trim() : '';
  const actions = actionMatches.map(m => m[1].trim()).filter(Boolean);

  if (actions.length === 0) return null;
  return { prompt, actions };
}

/**
 * Converts a parsed action block into an HTML string with data attributes
 * for event delegation. Wrapped in blank lines so `marked` treats it as
 * a raw HTML block rather than inline content.
 */
function blockToHtml(block) {
  let html = '<div class="mta-suggested-actions">';
  if (block.prompt) {
    html += `<p class="mta-suggested-prompt">${escapeHtml(block.prompt)}</p>`;
  }
  html += '<div class="mta-action-chips">';
  for (const action of block.actions) {
    html += `<button type="button" class="mta-action-chip" data-mta-action="${escapeHtml(action)}">${escapeHtml(action)}</button>`;
  }
  html += '</div></div>';
  return html;
}

/**
 * Transforms message content by replacing all complete <suggested-actions>
 * XML blocks with inline HTML. During streaming, incomplete blocks (opening
 * tag without closing tag) are silently stripped to prevent raw XML from
 * flashing in the UI.
 *
 * @param {string} content - Raw message content (Markdown + possible XML)
 * @param {boolean} [isStreaming=false] - Whether the message is still streaming
 * @returns {string} Content with XML blocks replaced by renderable HTML
 */
export function transformSuggestedActions(content, isStreaming = false) {
  if (!content) return content;

  // Reset global regex state for safe repeated calls
  BLOCK_PATTERN.lastIndex = 0;

  // Replace all complete XML blocks with inline HTML
  let result = content.replace(BLOCK_PATTERN, (_match, innerXml) => {
    const block = parseActionBlock(innerXml);
    if (!block) return '';
    // Blank lines ensure marked treats this as a standalone HTML block
    return '\n\n' + blockToHtml(block) + '\n\n';
  });

  // During streaming, strip any incomplete opening tag at the end
  if (isStreaming) {
    const lastOpenIndex = result.lastIndexOf(BLOCK_OPEN_TAG);
    if (lastOpenIndex !== -1) {
      const closingAfter = result.indexOf('</suggested-actions>', lastOpenIndex);
      if (closingAfter === -1) {
        result = result.slice(0, lastOpenIndex);
      }
    }
  }

  return result;
}
